'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { X, Check, Globe, MessageCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import Image from 'next/image'

// Platform icon/image mapping
const getPlatformIcon = (platform) => {
  switch (platform) {
    case 'iOS':
      return { type: 'image', src: '/icons/apple.svg', alt: 'iOS' }
    case 'Android':
      return { type: 'image', src: '/icons/android.svg', alt: 'Android' }
    case 'Chrome Extension':
      return { type: 'image', src: '/icons/chrome.svg', alt: 'Chrome' }
    case 'WhatsApp':
      return { type: 'image', src: '/icons/whatsapp.png', alt: 'WhatsApp' }
    case 'Web':
      return { type: 'icon', component: Globe }
    default:
      return { type: 'icon', component: Globe }
  }
}

export default function FeatureDetailModal({ feature, isOpen, onClose }) {
  const [videoLoading, setVideoLoading] = useState(true)
  const [imageLoading, setImageLoading] = useState(true)

  // Reset loading state when feature changes
  useEffect(() => {
    setVideoLoading(true)
    setImageLoading(true)
  }, [feature?.screenshot])

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  if (!feature) return null

  const FeatureIcon = feature.icon

  // Check if this is a Chrome Extension feature
  const isChromeExtension = feature.platform === "Chrome Extension" ||
                            (feature.platforms && feature.platforms.length === 1 && feature.platforms[0] === "Chrome Extension")

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
            <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-4xl bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
              >
                {/* Content - Mobile: stacked, Desktop: side-by-side */}
                <div className="flex flex-col md:flex-row">
                  {/* Left Side - Content */}
                  <div className="flex-1 p-6 sm:p-8 pb-12 md:max-h-[85vh] md:overflow-y-auto">
                    {/* Header */}
                    <div className="mb-6">
                      <div className="flex items-center gap-3 sm:gap-4 mb-4">
                        <div className={`w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                          <FeatureIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                        </div>
                        <div className="flex-1 pr-10">
                          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
                            {feature.title}
                          </h2>
                        </div>
                      </div>
                      <p className="text-base sm:text-lg text-slate-600 dark:text-slate-400">
                        {feature.detailedDescription || feature.description}
                      </p>
                    </div>

                    {/* Key Benefits */}
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
                        Key Benefits
                      </h3>
                      <ul className="space-y-3">
                        {feature.benefits?.map((benefit, index) => (
                          <motion.li
                            key={index}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            className="flex items-start gap-2 sm:gap-3"
                          >
                            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#2764FE]/10 dark:bg-[#4A7FFF]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Check className="w-3 h-3 sm:w-4 sm:h-4 text-[#2764FE] dark:text-[#4A7FFF]" />
                            </div>
                            <span className="text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
                              {benefit.split(/(\*\*.*?\*\*)/).map((part, i) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                  return <strong key={i} className="font-semibold text-slate-900 dark:text-white">{part.slice(2, -2)}</strong>
                                }
                                return part
                              })}
                            </span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>

                    {/* Platform Availability */}
                    {feature.platforms && feature.platforms.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                          Available On
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {feature.platforms.map((platform, index) => {
                            const platformIcon = getPlatformIcon(platform)
                            return (
                              <span
                                key={index}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs sm:text-sm font-medium rounded-lg border border-slate-200 dark:border-slate-700"
                              >
                                {platformIcon.type === 'image' ? (
                                  <Image
                                    src={platformIcon.src}
                                    alt={platformIcon.alt}
                                    width={20}
                                    height={20}
                                    className="w-5 h-5"
                                  />
                                ) : (
                                  <platformIcon.component className="w-5 h-5" />
                                )}
                                {platform}
                              </span>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Side - Mobile Screenshot/Video (hidden on mobile, full height on desktop) */}
                  <div className="hidden md:flex md:w-[45%] lg:w-[40%] bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 items-center justify-center p-6 md:max-h-[85vh] overflow-hidden">
                    {isChromeExtension ? (
                      /* Browser Window for Chrome Extension */
                      <div className="relative w-full">
                        {/* Browser chrome */}
                        <div className="relative bg-slate-900 rounded-t-xl overflow-hidden shadow-2xl">
                          {/* Browser header bar */}
                          <div className="bg-slate-800 px-4 py-3 flex items-center gap-2">
                            <div className="flex gap-1.5">
                              <div className="w-3 h-3 rounded-full bg-red-500"></div>
                              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                              <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            </div>
                            <div className="flex-1 mx-3">
                              <div className="bg-slate-700 rounded px-3 py-1 text-xs text-slate-400">
                                chrome-extension://ava
                              </div>
                            </div>
                          </div>
                          {/* Browser content */}
                          <div className="relative bg-white dark:bg-slate-950 overflow-hidden">
                            {feature.screenshot ? (
                              feature.screenshot.endsWith('.webm') || feature.screenshot.endsWith('.mp4') ? (
                                <div className="relative">
                                  {/* Loading skeleton for video */}
                                  {videoLoading && (
                                    <div className="absolute inset-0 flex items-center justify-center aspect-[9/19.5] bg-slate-100 dark:bg-slate-800">
                                      <div className="text-center">
                                        {/* Animated spinner */}
                                        <div className="inline-block w-12 h-12 border-4 border-slate-300 dark:border-slate-600 border-t-[#2764FE] rounded-full animate-spin mb-3"></div>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                          Loading video...
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                  <video
                                    src={feature.screenshot}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    onLoadedData={() => setVideoLoading(false)}
                                    onError={() => setVideoLoading(false)}
                                    className={`w-full h-auto transition-opacity duration-300 ${videoLoading ? 'opacity-0' : 'opacity-100'}`}
                                  />
                                </div>
                              ) : (
                                <div className="relative w-full aspect-[9/19.5]">
                                  {/* Loading skeleton for image */}
                                  {imageLoading && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                                      <div className="text-center">
                                        {/* Animated spinner */}
                                        <div className="inline-block w-12 h-12 border-4 border-slate-300 dark:border-slate-600 border-t-[#2764FE] rounded-full animate-spin mb-3"></div>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                          Loading image...
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                  <Image
                                    src={feature.screenshot}
                                    alt={`${feature.title} screenshot`}
                                    fill
                                    className={`object-cover transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                                    onLoad={() => setImageLoading(false)}
                                    onError={() => setImageLoading(false)}
                                  />
                                </div>
                              )
                            ) : (
                              <div className="flex items-center justify-center aspect-[9/19.5]">
                                <div className="text-center p-4">
                                  <FeatureIcon className="w-16 h-16 text-slate-400 dark:text-slate-600 mx-auto mb-2" />
                                  <p className="text-sm text-slate-500 dark:text-slate-500">
                                    Screenshot coming soon
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Phone Frame for Mobile Apps */
                      <div className="relative w-full max-w-[260px]">
                        {/* Phone outer frame */}
                        <div className="relative bg-slate-900 rounded-[2rem] p-1.5 shadow-2xl">
                          {/* Screen bezel */}
                          <div className="relative bg-black rounded-[1.75rem] overflow-hidden">
                            {/* Screen content */}
                            <div className="relative bg-white dark:bg-slate-950 overflow-hidden">
                              {feature.screenshot ? (
                                feature.screenshot.endsWith('.webm') || feature.screenshot.endsWith('.mp4') ? (
                                  <div className="relative">
                                    {/* Loading skeleton for video */}
                                    {videoLoading && (
                                      <div className="absolute inset-0 flex items-center justify-center aspect-[9/19.5] bg-slate-100 dark:bg-slate-800">
                                        <div className="text-center">
                                          {/* Animated spinner */}
                                          <div className="inline-block w-12 h-12 border-4 border-slate-300 dark:border-slate-600 border-t-[#2764FE] rounded-full animate-spin mb-3"></div>
                                          <p className="text-sm text-slate-500 dark:text-slate-400">
                                            Loading video...
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                    <video
                                      src={feature.screenshot}
                                      autoPlay
                                      loop
                                      muted
                                      playsInline
                                      onLoadedData={() => setVideoLoading(false)}
                                      onError={() => setVideoLoading(false)}
                                      className={`w-full h-auto transition-opacity duration-300 ${videoLoading ? 'opacity-0' : 'opacity-100'}`}
                                    />
                                  </div>
                                ) : (
                                  <div className="relative w-full aspect-[9/19.5]">
                                    {/* Loading skeleton for image */}
                                    {imageLoading && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                                        <div className="text-center">
                                          {/* Animated spinner */}
                                          <div className="inline-block w-12 h-12 border-4 border-slate-300 dark:border-slate-600 border-t-[#2764FE] rounded-full animate-spin mb-3"></div>
                                          <p className="text-sm text-slate-500 dark:text-slate-400">
                                            Loading image...
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                    <Image
                                      src={feature.screenshot}
                                      alt={`${feature.title} screenshot`}
                                      fill
                                      className={`object-cover transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                                      onLoad={() => setImageLoading(false)}
                                      onError={() => setImageLoading(false)}
                                    />
                                  </div>
                                )
                              ) : (
                                <div className="flex items-center justify-center aspect-[9/19.5]">
                                  <div className="text-center p-4">
                                    <FeatureIcon className="w-16 h-16 text-slate-400 dark:text-slate-600 mx-auto mb-2" />
                                    <p className="text-sm text-slate-500 dark:text-slate-500">
                                      Screenshot coming soon
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Side buttons */}
                          <div className="absolute -left-1 top-24 w-1 h-8 bg-slate-800 rounded-l"></div>
                          <div className="absolute -left-1 top-36 w-1 h-12 bg-slate-800 rounded-l"></div>
                          <div className="absolute -right-1 top-28 w-1 h-16 bg-slate-800 rounded-r"></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}
